import Phaser from "phaser";
import { ARENA_HEIGHT, ARENA_WIDTH, EMPTY_INPUT, type ActionInput, type MatchState } from "../game/simulation";
import { ArenaScene } from "./scenes/ArenaScene";
import { SceneBridge } from "./sceneBridge";

export interface ArenaRendererOptions {
  parent: HTMLElement;
  readInput?: () => ActionInput;
  onInput?: (input: ActionInput) => void;
  compact?: boolean;
}

export class ArenaRenderer {
  readonly bridge = new SceneBridge();
  readonly game: Phaser.Game;
  private unsubscribeInput: (() => void) | null = null;

  constructor(options: ArenaRendererOptions) {
    const scene = new ArenaScene({
      bridge: this.bridge,
      readInput: options.readInput ?? (() => EMPTY_INPUT),
      compact: options.compact ?? false
    });

    if (options.onInput) {
      this.unsubscribeInput = this.bridge.onInput(options.onInput);
    }

    this.game = new Phaser.Game({
      // Admin can render many concurrent cards; Canvas avoids exhausting the
      // browser's small pool of WebGL contexts while normal play stays WebGL.
      type: options.compact ? Phaser.CANVAS : Phaser.AUTO,
      parent: options.parent,
      width: ARENA_WIDTH,
      height: ARENA_HEIGHT,
      backgroundColor: "#071422",
      scene,
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: ARENA_WIDTH,
        height: ARENA_HEIGHT
      },
      render: {
        antialias: true,
        pixelArt: false,
        roundPixels: true
      },
      input: {
        activePointers: 4,
        gamepad: true
      },
      fps: {
        target: 60,
        smoothStep: true
      }
    });
  }

  publish(state: MatchState): void {
    this.bridge.publishSnapshot(state);
  }

  setPaused(paused: boolean): void {
    if (paused) {
      this.game.loop.sleep();
    } else {
      this.game.loop.wake();
    }
  }

  destroy(): void {
    this.unsubscribeInput?.();
    this.unsubscribeInput = null;
    this.game.destroy(true);
  }
}
